import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
	useDailyEvent,
	useParticipantIds,
	useParticipant,
	useScreenShare,
} from '@daily-co/daily-react-hooks';
import { alpha, Box, IconButton, Stack } from '@mui/material';
import { theme } from '../../../../common/styles/theme';
import { useCallState } from '../shared/contexts/CallProvider';
import { usePip } from '../shared/contexts/PipProvider';
import { PipIcon } from '../../../../../icons/PipIcon';
import { MicStatus } from '../shared/components/MicStatus/MicStatus';

export const SpeakerView: React.FC = () => {
	const className = 'speaker-view';

	const { callObject } = useCallState();
	const { togglePip, isPipSupported } = usePip();

	const [myId, setMyId] = useState<string | null>(null);
	const [activeSpeakerId, setActiveSpeakerId] = useState<string | null>(null);
	const [ghostIds, setGhostIds] = useState<Set<string>>(() => new Set());

	const { screens } = useScreenShare();
	const screenSharerId = screens[0]?.session_id || null;
	const isLocalSharing = !!myId && screenSharerId === myId;

	const focusedId = (screenSharerId && !isLocalSharing) ? screenSharerId : activeSpeakerId;

	// Unfiltered list, used to tell whether focusedId still refers to someone in the call
	// (participantIds below excludes focusedId by design, so it can't answer that).
	const allParticipantIds = useParticipantIds({ sort: 'joined_at' });

	// A hard disconnect (e.g. a page refresh) doesn't reliably fire 'participant-left' for the
	// dead session — it can sit in the participant list indefinitely alongside the same person's
	// new session. Guests never get a meeting token (see conferenceJoinRoomAction), so Daily's own
	// user_id is never set for them and can't be used; userData is client-set at join() (see
	// useCallMachine) and syncs to every participant regardless of token/owner status.
	const rawParticipants = callObject?.participants();
	// The local participant is keyed as `local`, NOT by its own session_id, in DailyParticipantsObject —
	// indexing by id alone silently misses it, which is why it needs its own lookup here.
	const getParticipant = (id: string) => rawParticipants?.local?.session_id === id
		? rawParticipants.local
		: rawParticipants?.[id];
	const getUserId = (p: ReturnType<typeof getParticipant>) =>
		(p?.userData as { userId?: string } | undefined)?.userId || null;

	const latestSessionByUser = new Map<string, string>();
	allParticipantIds.forEach((id) => {
		// Local is included here on purpose: after a refresh, the room roster still lists your own
		// old session as a *remote* participant with your own userId — it needs your local session
		// as competition to lose against, otherwise it looks like an unrelated live participant.
		const p = getParticipant(id);
		const userId = getUserId(p);
		if (!p || !userId) return;
		const bestId = latestSessionByUser.get(userId);
		const best = bestId ? getParticipant(bestId) : undefined;
		if (!best || (!!p.joined_at && !!best.joined_at && p.joined_at > best.joined_at)) {
			latestSessionByUser.set(userId, id);
		}
	});
	// Once a session has been outranked by a newer session from the same account, it stays a ghost
	// for good — otherwise it resurfaces the moment the newer session does a clean Leave, since on
	// its own (nothing left to lose to) it would look live again. Setting state during render
	// (rather than in an effect) keeps this render's own output consistent instead of lagging a
	// frame behind — the same "adjusting state during render" pattern React docs use for
	// resetting state from a prop change.
	const newlyGhosted = allParticipantIds.filter((id) => {
		const p = getParticipant(id);
		const userId = getUserId(p);
		return !!p && !p.local && !!userId && latestSessionByUser.get(userId) !== id && !ghostIds.has(id);
	});
	if (newlyGhosted.length > 0) {
		setGhostIds((prev) => {
			const next = new Set(prev);
			newlyGhosted.forEach((id) => next.add(id));
			return next;
		});
	}
	const isGhost = (id: string) => ghostIds.has(id);

	const participantIds = useParticipantIds({
		filter: (p) => { // If remote is sharing, show all participants. Else don't show focused.
			if (isGhost(p.session_id)) return false;
			return (!!screenSharerId && !isLocalSharing) || p.session_id !== focusedId;
		},
		sort: 'joined_at',
	});
	const screenTileId = isLocalSharing && myId ? `${myId}::screen` : null;
	const thumbnailIds = screenTileId ? [screenTileId, ...participantIds] : participantIds;
	const thumbnailsRowCount = thumbnailIds.length === 1
		? 2
		: Math.max(thumbnailIds.length, 1);

	const largeVideoRef = useRef<HTMLVideoElement | null>(null);
	const activeParticipant = useParticipant(focusedId || '');

	useDailyEvent('active-speaker-change', (e) => {
		const id = e?.activeSpeaker?.peerId;
		if (!!id && id !== myId) {
			setActiveSpeakerId(id);
		}
	});

	// The participant object is replaced on every participant-updated event,
	// so depend on the track itself to avoid re-attaching (and flickering) the same video.
	const activeScreenTrack = activeParticipant?.tracks.screenVideo?.state === 'playable'
		? activeParticipant.tracks.screenVideo.persistentTrack || null
		: null;
	const activeCamTrack = activeParticipant?.tracks.video?.state === 'playable'
		? activeParticipant.tracks.video.persistentTrack || null
		: null;
	const activeTrack = activeScreenTrack || activeCamTrack;

	// activeSpeaker tracks
	useEffect(() => {
		const videoEl = largeVideoRef.current;
		if (!videoEl) return;

		if (!activeTrack) {
			videoEl.srcObject = null;
			return;
		}

		const current = videoEl.srcObject as MediaStream | null;
		if (current && current.getVideoTracks()[0]?.id === activeTrack.id) return;

		videoEl.srcObject = new MediaStream([activeTrack]);
		videoEl.play().catch(() => { });
	}, [activeTrack, activeTrack?.id]);

	const liveParticipantIds = useMemo(
		() => allParticipantIds.filter((id) => !ghostIds.has(id)),
		[allParticipantIds, ghostIds],
	);

	useEffect(() => {
		// liveParticipantIds already excludes ghosts, so absence alone covers both "left" and "ghost".
		const focusedIsGone = !!activeSpeakerId && !liveParticipantIds.includes(activeSpeakerId);
		if ((!activeSpeakerId || activeSpeakerId === myId || focusedIsGone) &&
			!!liveParticipantIds.length
		) {
			const next = liveParticipantIds.find((id) => id !== myId) || liveParticipantIds[0];
			setActiveSpeakerId(next);
		}
	}, [liveParticipantIds, myId]);

	useEffect(() => {
		const me = callObject?.participants().local;
		const myId = me?.session_id || null
		setMyId(myId);
		if (myId && !activeSpeakerId) setActiveSpeakerId(myId);
	}, []);

	return (
		<Stack
			className={className}
			flexDirection={'row'}
			gap={1}
			sx={{
				height: 1,
			}}
		>
			<Box
				className={`${className}__active`}
				sx={{
					position: 'relative',
					flex: `1 1 100%`,
					minHeight: 0,
					display: 'flex',
					flexDirection: 'column',
					gap: .25,
					fontSize: theme.typography.subtitle1,
				}}
			>
				<Box
					component={'video'}
					className={`${className}__active-video`}
					ref={largeVideoRef}
					autoPlay
					playsInline
					sx={{
						width: 1,
						height: 1,
						flex: `1 1 100%`,
						minHeight: 0,
						background: theme.palette.grey[900],
						borderRadius: theme.spacing(.75),
						transform: (activeParticipant?.local && !isLocalSharing) ? 'scaleX(-1)' : 'none',
					}}
				/>
				<Box
					sx={{
						position: 'absolute',
						left: 0,
						right: 0,
						bottom: 0,
						p: theme.spacing(1),
						display: 'flex',
						alignItems: 'center',
						gap: .5,
						color: theme.palette.common.white,
						textShadow: `0 0 8px ${theme.palette.grey[900]}`,
					}}
				>
					{focusedId && <MicStatus sessionId={focusedId} />}
					<Box sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
						{activeParticipant?.user_name || activeParticipant?.session_id || '—'}
					</Box>
				</Box>

				{isPipSupported &&
					<IconButton
						onClick={togglePip}
						sx={{
							position: 'absolute',
							right: theme.spacing(2),
							top: theme.spacing(2),
							width: theme.spacing(5),
							height: theme.spacing(5),
							color: theme.palette.common.white,
							background: alpha(theme.palette.grey[900], .1),
							borderRadius: '50%',

							'svg': {
								width: theme.spacing(3),
								height: theme.spacing(3),
							},

							'&:hover': {
								background: alpha(theme.palette.grey[900], .7),
							},

						}}
					>
						<PipIcon />
					</IconButton>
				}
			</Box>

			<Stack
				className={`${className}__thumbnails`}
				gap={1}
				sx={{
					flex: `1 1 30vw`,
					display: `grid`,
					gap: 1,
					height: 1,
					minHeight: 0,
					gridTemplateColumns: 'minmax(0, 1fr)',
					gridTemplateRows: `repeat(${thumbnailsRowCount}, minmax(0, 1fr))`,
					overflow: 'hidden',
				}}
			>
				{thumbnailIds.map((id) => (
					<Thumbnail key={id} id={id} />
				))}
			</Stack>
		</Stack>
	);
};

const Thumbnail: React.FC<{ id: string }> = ({ id }) => {
	const className = 'speaker-view';

	const [sessionId, kind] = id.split('::');
	const isScreen = kind === 'screen';
	const p = useParticipant(sessionId);
	const vRef = useRef<HTMLVideoElement | null>(null);

	const source = isScreen ? p?.tracks.screenVideo : p?.tracks.video;
	const videoTrack = source?.state === 'playable' ? source.persistentTrack || null : null;
	const audioTrack = !isScreen && p?.tracks.audio?.state === 'playable'
		? p.tracks.audio.persistentTrack || null
		: null;

	useEffect(() => {
		const videoEl = vRef.current;
		if (!videoEl) return;

		if (!videoTrack && !audioTrack) {
			videoEl.srcObject = null;
			return;
		}

		// Skip re-attaching when the same tracks are already playing — that causes flickering.
		const current = videoEl.srcObject as MediaStream | null;
		const sameVideo = (current?.getVideoTracks()[0]?.id || null) === (videoTrack?.id || null);
		const sameAudio = (current?.getAudioTracks()[0]?.id || null) === (audioTrack?.id || null);
		if (current && sameVideo && sameAudio) return;

		const stream = new MediaStream();
		if (videoTrack) stream.addTrack(videoTrack);
		if (audioTrack) stream.addTrack(audioTrack);

		videoEl.srcObject = stream;
		videoEl.muted = true;
		videoEl.play().catch(() => { });
	}, [videoTrack, videoTrack?.id, audioTrack, audioTrack?.id]);

	return (
		<Box
			className={`${className}__thumbnail`}
			sx={{
				position: 'relative',
				minHeight: 0,
				height: 1,
				fontSize: theme.typography.subtitle1,
				lineHeight: 1.4,
				borderRadius: theme.spacing(.75),
				overflow: 'hidden',
				background: theme.palette.grey[900],
			}}
		>
			<Box
				component={'video'}
				className={`${className}__thumbnail-video`}
				ref={vRef}
				autoPlay
				playsInline
				sx={{
					width: 1,
					height: 1,
					objectFit: isScreen ? 'contain' : 'cover',
					transform: (p?.local && !isScreen) ? 'scaleX(-1)' : 'none',
				}}
			/>
			<Box
				className={`${className}__thumbnail-title`}
				sx={{
					position: 'absolute',
					left: 0,
					right: 0,
					bottom: 0,
					p: theme.spacing(1),
					display: 'flex',
					alignItems: 'center',
					gap: .5,
					color: theme.palette.common.white,
					textShadow: `0 0 8px ${theme.palette.grey[900]}`,
				}}
			>
				{!isScreen && <MicStatus sessionId={sessionId} />}
				<Box sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
					{p?.user_name || p?.session_id || 'unknown'}
				</Box>
			</Box>
		</Box>
	);
};
